import { toLoggableEditorError } from '@/components/tiptap/utils/loggable-editor-error';
import { type NoteEditorType } from '@/models/analytics/features/notes';
import { logger } from '@/utils/logger/logger';
import {
  applyContentPreservingSelection as applyPreservingSelection,
  toSafeEditorErrorSummary,
} from '@heidi/editor/core';
import type { Editor } from '@tiptap/react';

import {
  emitNoteEditorRenderFailedOnce,
  SELECTION_RESTORE_FAILED_CODE,
  SET_CONTENT_FAILED_CODE,
  type NoteEditorRenderFailedCode,
  type NoteEditorSurface,
} from '../telemetry/emit-note-editor-render-failed';

/**
 * `editorVariant` is required because this util is shared: an absent one would
 * make a vMarkdown failure indistinguishable from a block-v1 one.
 */
export interface ApplyContentContext {
  editorVariant: NoteEditorType;
  contentLength: number;
  sessionId?: string;
  /** Note-tab SLI membership. Only the note tab supplies it. */
  surface?: NoteEditorSurface;
}

function emitRenderFailureOnce(
  editor: Editor,
  { editorVariant, contentLength, surface }: ApplyContentContext,
  errorCode: NoteEditorRenderFailedCode
): void {
  emitNoteEditorRenderFailedOnce(editor, {
    editorType: editorVariant,
    // This shell only applies HTML; markdown-native emits via its own adapter.
    contentType: 'html',
    contentLength,
    errorCode,
    surface,
  });
}

/**
 * App-side shell over the pure apply in `@heidi/editor/core`, which reports
 * failures instead of logging: this wrapper owns the log and the RUM action.
 */
export function applyContentPreservingSelection(
  editor: Editor,
  setContent: () => void,
  onSetContentError: (error: unknown) => void,
  context: ApplyContentContext
): void {
  applyPreservingSelection(editor, setContent, onSetContentError, (summary) => {
    // Typed safe envelope only (task 4.10): raw editor errors can echo
    // document content and must never reach app logs.
    logger.error('Failed to restore editor selection after setContent', {
      ...context,
      error: toLoggableEditorError(summary, 'selection_restore_failed'),
    });
    // The content did render — only the caret restore threw. Its own code so a
    // render-success SLI can exclude it.
    emitRenderFailureOnce(editor, context, SELECTION_RESTORE_FAILED_CODE);
  });
}

/** HTML content sync (whitespace preserved, invalid content tolerated). */
export function applyHtmlContentPreservingSelection(
  editor: Editor,
  content: string,
  context: Omit<ApplyContentContext, 'contentLength'>
): void {
  const failureContext: ApplyContentContext = {
    ...context,
    contentLength: content.length,
  };

  applyContentPreservingSelection(
    editor,
    () => {
      editor.commands.setContent(content, {
        emitUpdate: false,
        parseOptions: {
          preserveWhitespace: 'full',
        },
        errorOnInvalidContent: false,
      });
    },
    (error) => {
      logger.error('Failed to set editor content', {
        ...failureContext,
        error: toLoggableEditorError(
          toSafeEditorErrorSummary(error),
          'content_apply_failed'
        ),
      });
      emitRenderFailureOnce(editor, failureContext, SET_CONTENT_FAILED_CODE);
    },
    failureContext
  );
}
